import { useState, useCallback } from "react";
import { useScribe } from "@elevenlabs/react";

export type TranscriptionStatus = "idle" | "recording" | "error";

export interface UseAudioTranscriptionOptions {
  tokenUrl?: string;
}

export interface UseAudioTranscriptionResult {
  start: () => Promise<void>;
  stop: () => void;
  transcript: string;
  status: TranscriptionStatus;
}

export function useAudioTranscription({
  tokenUrl = "/api/notes/scribe-token/",
}: UseAudioTranscriptionOptions = {}): UseAudioTranscriptionResult {
  const [committed, setCommitted] = useState("");
  const [status, setStatus] = useState<TranscriptionStatus>("idle");

  const scribe = useScribe({
    modelId: "scribe_v2_realtime",
    onCommittedTranscript: (data) => {
      setCommitted((prev) => (prev ? `${prev} ${data.text}` : data.text));
    },
  });

  const transcript = committed
    ? committed +
      (scribe.partialTranscript ? ` ${scribe.partialTranscript}` : "")
    : (scribe.partialTranscript ?? "");

  const start = useCallback(async () => {
    setCommitted("");
    setStatus("recording");
    try {
      const res = await fetch(tokenUrl);
      const { token } = (await res.json()) as { token: string };
      await scribe.connect({
        token,
        microphone: { echoCancellation: true, noiseSuppression: true },
      });
    } catch {
      setStatus("error");
    }
  }, [tokenUrl, scribe]);

  const stop = useCallback(() => {
    scribe.disconnect();
    setStatus("idle");
  }, [scribe]);

  return { start, stop, transcript, status };
}
